import { t } from '@transifex/native';

import { flashes } from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import * as sdk from 'pkg/core/sdk';

export type PinItemType = 'collection' | 'session' | 'exercise';

export type ShareItemType = 'collection' | 'session' | 'exercise';

const shareEndpointByType: Record<
	ShareItemType,
	(orgId: number, id: number) => string
> = {
	collection: endpoints.TrainingCollections.UpdateOrganizationShare,
	session: endpoints.TrainingSessions.UpdateOrganizationShare,
	exercise: endpoints.Exercises.UpdateOrganizationShare,
};

export const shareWithOrganizations = async (
	itemType: ShareItemType,
	organizationId: number,
	id: number,
	targetOrganizationIds: number[]
): Promise<boolean> => {
	const response = await sdk.put(
		shareEndpointByType[itemType](organizationId, id),
		{},
		{ targetOrganizationIds }
	);

	if (!response.ok) {
		flashes.show({
			title: t('Something went wrong!'),
			message: response.statusText,
		});

		return false;
	}

	flashes.show({
		title: t('Success!'),
		message: t('Shared organizations updated.'),
	});

	return true;
};

export const bulkPin = async (
	organizationId: number,
	collectionIds: number[],
	sessionIds: number[],
	exerciseIds: number[]
): Promise<boolean> => {
	const response = await sdk.post(
		endpoints.TrainingLibrary.BulkPin(organizationId),
		{},
		{ collectionIds, sessionIds, exerciseIds }
	);

	if (!response.ok) {
		flashes.show({
			title: t('Something went wrong!'),
			message: t('Could not pin the selected items.'),
		});

		return false;
	}

	return true;
};

export const bulkUnpin = async (
	organizationId: number,
	collectionIds: number[],
	sessionIds: number[],
	exerciseIds: number[]
): Promise<boolean> => {
	const response = await sdk.post(
		endpoints.TrainingLibrary.BulkUnpin(organizationId),
		{},
		{ collectionIds, sessionIds, exerciseIds }
	);

	if (!response.ok) {
		flashes.show({
			title: t('Something went wrong!'),
			message: t('Could not unpin the selected items.'),
		});

		return false;
	}

	return true;
};

export const togglePin = async (
	organizationId: number,
	itemType: PinItemType,
	id: number,
	pinned: boolean
): Promise<boolean> => {
	const endpoint = pinned
		? endpoints.TrainingLibrary.Unpin(organizationId, itemType, id)
		: endpoints.TrainingLibrary.Pin(organizationId, itemType, id);

	const response = pinned
		? await sdk.destroy(endpoint)
		: await sdk.post(endpoint);

	if (!response.ok) {
		flashes.show({
			title: t('Something went wrong!'),
			message: pinned ? t('Could not unpin item.') : t('Could not pin item.'),
		});

		return false;
	}

	return true;
};
